import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ReportFormData } from "../../types/ReportFormData";
import { DailyWorkingHours } from "../../types/DailyWorkingHours";
import { Day } from "../../types/Day";
import { OfflineLog } from "../../types/OfflineLog";
import { Monitor } from "../../types/Screen";
import { Holiday } from "../../types/Holiday";
import Input from "../ui/Input";
import DatePicker from "../ui/DatePicker";
import Button from "../ui/Button";
import RenameScreensSection from "./RenameScreensSection";
import GroupScreensSection from "./GroupScreensSection";
import { DAY_NAME_KEYS } from "../../utils/constants";
import { translateErrorMessage } from "../../utils/translateError";

interface AdvancedFilterSectionProps {
	formData: ReportFormData;
	onChange: (data: Partial<ReportFormData>) => void;
	errors?: Partial<Record<keyof ReportFormData, string>>;
	screenOptions: Monitor[];
	loadingScreens: boolean;
	holidays: Holiday[];
	loadingHolidays: boolean;
	holidaysError: string | null;
}

const AdvancedFilterSection: React.FC<AdvancedFilterSectionProps> = ({
	formData,
	onChange,
	errors,
	screenOptions,
	loadingScreens,
	holidays,
	loadingHolidays,
	holidaysError,
}) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const [useCustomWorkingHours, setUseCustomWorkingHours] = useState(false);

	const handleFieldChange = (field: keyof ReportFormData, value: any) => {
		onChange({ [field]: value });
	};

	const handleAddExcludedDate = () => {
		const newDate = new Date();
		handleFieldChange("specific_dates_to_exclude", [
			...formData.specific_dates_to_exclude,
			newDate,
		]);
	};

	const handleRemoveExcludedDate = (index: number) => {
		const updatedDates = formData.specific_dates_to_exclude.filter(
			(_, i) => i !== index,
		);
		handleFieldChange("specific_dates_to_exclude", updatedDates);
	};

	const handleExcludedDateChange = (index: number, date: Date | null) => {
		const updatedDates = [...formData.specific_dates_to_exclude];
		updatedDates[index] = date || new Date();
		handleFieldChange("specific_dates_to_exclude", updatedDates);
	};

	const handleWorkingHoursChange = (
		day: Day,
		field: "starting_hours" | "ending_hours",
		value: string,
	) => {
		const updated = formData.daily_working_hours.map((wh) =>
			wh.day === day ? { ...wh, [field]: value } : wh,
		);
		handleFieldChange("daily_working_hours", updated);
	};

	const getWorkingHoursForDay = (day: Day): DailyWorkingHours | undefined => {
		return formData.daily_working_hours.find((wh) => wh.day === day);
	};

	const handleCustomWorkingHoursToggle = (checked: boolean) => {
		setUseCustomWorkingHours(checked);
		if (checked) {
			const updatedHours: DailyWorkingHours[] = [];
			formData.days.forEach((day) => {
				const existing = formData.daily_working_hours.find(
					(wh) => wh.day === day,
				);
				if (existing) {
					updatedHours.push(existing);
				} else {
					updatedHours.push({
						day,
						starting_hours: "09:00",
						ending_hours: "17:00",
					});
				}
			});
			handleFieldChange("daily_working_hours", updatedHours);
		} else {
			handleFieldChange("daily_working_hours", []);
		}
	};

	useEffect(() => {
		if (useCustomWorkingHours) {
			const updatedHours: DailyWorkingHours[] = [];
			formData.days.forEach((day) => {
				const existing = formData.daily_working_hours.find(
					(wh) => wh.day === day,
				);
				if (existing) {
					updatedHours.push(existing);
				} else {
					updatedHours.push({
						day,
						starting_hours: "09:00",
						ending_hours: "17:00",
					});
				}
			});
			const currentDays = formData.daily_working_hours
				.map((wh) => wh.day)
				.sort();
			const newDays = updatedHours.map((wh) => wh.day).sort();
			const daysChanged =
				JSON.stringify(currentDays) !== JSON.stringify(newDays);
			if (daysChanged) {
				handleFieldChange("daily_working_hours", updatedHours);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formData.days, useCustomWorkingHours]);

	const handleAddLog = () => {
		const newLog: OfflineLog = {
			screen_id: screenOptions.length > 0 ? screenOptions[0].Monitor_ID : 0,
			hours_logged: 0,
			ad_plays: 0,
		};
		handleFieldChange("offline_logs", [...formData.offline_logs, newLog]);
	};

	const handleRemoveLog = (index: number) => {
		const updatedLogs = formData.offline_logs.filter((_, i) => i !== index);
		handleFieldChange("offline_logs", updatedLogs);
	};

	const handleLogChange = (
		index: number,
		field: keyof OfflineLog,
		value: string | number,
	) => {
		const updatedLogs = [...formData.offline_logs];
		updatedLogs[index] = {
			...updatedLogs[index],
			[field]: field === "screen_id" ? Number(value) : value,
		};
		handleFieldChange("offline_logs", updatedLogs);
	};

	const screensReady = !loadingScreens && screenOptions.length > 0;

	return (
		<div className="pb-6 border-b border-gray-200">
			<button
				type="button"
				onClick={() => setIsExpanded(!isExpanded)}
				className="w-full flex items-center justify-between py-4 text-left outline-none focus:outline-none rounded-xl px-2 -mx-2 hover:bg-gray-50 transition-all duration-200"
			>
				<h2 className="text-2xl font-bold text-gray-800">
					{t("form.advanced.title")}
				</h2>
				<svg
					className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${
						isExpanded ? "transform rotate-180" : ""
					}`}
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M19 9l-7 7-7-7"
					/>
				</svg>
			</button>

			{isExpanded && (
				<div className="space-y-6 pt-2">
					<label
						htmlFor="exclude_holidays"
						className={`flex items-center cursor-pointer px-4 py-2 rounded-lg border-2 transition-all duration-200 w-fit outline-none focus:outline-none ${
							formData.exclude_holidays
								? "bg-gradient-to-r from-primary-50 to-primary-100 border-primary-400 text-primary-700 font-semibold shadow-sm"
								: "bg-white border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-primary-50/50"
						}`}
					>
						<input
							type="checkbox"
							id="exclude_holidays"
							checked={formData.exclude_holidays}
							onChange={(e) =>
								handleFieldChange("exclude_holidays", e.target.checked)
							}
							className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-0 focus:outline-none mr-2"
						/>
						<span>{t("form.advanced.excludeHolidays")}</span>
					</label>

					{formData.exclude_holidays && (
						<div className="rounded-lg border border-primary-100 bg-primary-50/50 p-4">
							<p className="text-sm font-semibold text-gray-700 mb-2">
								{t("form.advanced.holidaysInRange")}
							</p>
							{!formData.start_date || !formData.end_date ? (
								<p className="text-sm text-gray-500">
									{t("form.advanced.selectDatesForHolidays")}
								</p>
							) : loadingHolidays ? (
								<p className="text-sm text-gray-500">
									{t("form.advanced.loadingHolidays")}
								</p>
							) : holidaysError ? (
								<p className="text-sm text-red-600">
									{translateErrorMessage(
										holidaysError,
										"errors.loadHolidaysFailed",
									)}
								</p>
							) : holidays.length === 0 ? (
								<p className="text-sm text-gray-500">
									{t("form.advanced.noHolidays")}
								</p>
							) : (
								<ul className="space-y-1 text-sm text-gray-700">
									{holidays.map((holiday, index) => (
										<li key={`${holiday.date || holiday.name}-${index}`}>
											{holiday.date ? (
												<span className="font-medium">{holiday.date}: </span>
											) : null}
											<span>{holiday.name}</span>
										</li>
									))}
								</ul>
							)}
						</div>
					)}

					<div className="border-t border-gray-200"></div>

					<div className="space-y-4">
						<label
							htmlFor="use_custom_working_hours"
							className={`flex items-center cursor-pointer px-4 py-2 rounded-lg border-2 transition-all duration-200 w-fit mb-3 outline-none focus:outline-none ${
								useCustomWorkingHours
									? "bg-gradient-to-r from-primary-50 to-primary-100 border-primary-400 text-primary-700 font-semibold shadow-sm"
									: "bg-white border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-primary-50/50"
							}`}
						>
							<input
								type="checkbox"
								id="use_custom_working_hours"
								checked={useCustomWorkingHours}
								onChange={(e) =>
									handleCustomWorkingHoursToggle(e.target.checked)
								}
								className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-0 focus:outline-none mr-2"
							/>
							<span>{t("form.advanced.customWorkingHours")}</span>
						</label>

						{useCustomWorkingHours && (
							<>
								{formData.days.length === 0 ? (
									<p className="text-sm text-gray-500">
										{t("form.advanced.selectDaysForHours")}
									</p>
								) : (
									<div className="space-y-3">
										{formData.days.map((day) => {
											const workingHours = getWorkingHoursForDay(day);
											if (!workingHours) return null;

											return (
												<div
													key={day}
													className="p-4 border border-gray-200 rounded-lg"
												>
													<h3 className="font-semibold text-gray-800 mb-3">
														{t(DAY_NAME_KEYS[day])}
													</h3>
													<div className="grid grid-cols-2 gap-4">
														<Input
															label={t("form.advanced.startingHours")}
															type="time"
															value={workingHours.starting_hours}
															onChange={(e) =>
																handleWorkingHoursChange(
																	day,
																	"starting_hours",
																	e.target.value,
																)
															}
														/>
														<Input
															label={t("form.advanced.endingHours")}
															type="time"
															value={workingHours.ending_hours}
															onChange={(e) =>
																handleWorkingHoursChange(
																	day,
																	"ending_hours",
																	e.target.value,
																)
															}
														/>
													</div>
												</div>
											);
										})}
									</div>
								)}
							</>
						)}
					</div>

					<div className="border-t border-gray-200"></div>

					<div className="space-y-3">
						<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
							<label className="block text-gray-700 text-sm font-bold">
								{t("form.advanced.specificDatesToExclude")}
							</label>
							<Button
								type="button"
								size="sm"
								variant="secondary"
								onClick={handleAddExcludedDate}
								fullWidth={false}
								className="w-full sm:w-auto"
							>
								{t("form.advanced.addDate")}
							</Button>
						</div>

						{formData.specific_dates_to_exclude.length === 0 ? (
							<p className="text-sm text-gray-500">
								{t("form.advanced.noExcludedDates")}
							</p>
						) : (
							<div className="space-y-2">
								{formData.specific_dates_to_exclude.map((date, index) => (
									<div
										key={index}
										className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50"
									>
										<DatePicker
											value={date as any}
											onChange={(e) => {
												const dateValue = e.target.value
													? new Date(e.target.value)
													: null;
												handleExcludedDateChange(index, dateValue);
											}}
											fullWidth={true}
											className="flex-1"
										/>
										<Button
											type="button"
											size="sm"
											variant="danger"
											onClick={() => handleRemoveExcludedDate(index)}
											fullWidth={false}
											className="w-full sm:w-auto"
										>
											{t("common.remove")}
										</Button>
									</div>
								))}
							</div>
						)}
					</div>

					<div className="border-t border-gray-200"></div>

					<RenameScreensSection
						formData={formData}
						onChange={onChange}
						screenOptions={screenOptions}
					/>

					<div className="border-t border-gray-200"></div>

					<GroupScreensSection
						formData={formData}
						onChange={onChange}
						screenOptions={screenOptions}
					/>

					<div className="border-t border-gray-200"></div>

					<div className="space-y-3">
						<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
							<label className="block text-gray-700 text-sm font-bold">
								{t("form.advanced.offlineLogs")}
							</label>
							<Button
								type="button"
								size="sm"
								variant="secondary"
								onClick={handleAddLog}
								fullWidth={false}
								className="w-full sm:w-auto"
							>
								{t("form.advanced.addOfflineLog")}
							</Button>
						</div>

						{formData.offline_logs.length === 0 ? (
							<p className="text-sm text-gray-500">
								{t("form.advanced.noOfflineLogs")}
							</p>
						) : (
							<div className="space-y-4">
								{formData.offline_logs.map((log, index) => (
									<div
										key={index}
										className="p-5 border border-gray-200 rounded-xl bg-gradient-to-br from-gray-50 to-primary-50/30 shadow-sm hover:shadow-md transition-shadow"
									>
										<div className="flex items-center justify-between mb-4">
											<h3 className="font-semibold text-gray-800">
												{t("form.advanced.offlineLogTitle", {
													number: index + 1,
												})}
											</h3>
											<Button
												type="button"
												size="sm"
												variant="danger"
												onClick={() => handleRemoveLog(index)}
											>
												{t("common.remove")}
											</Button>
										</div>

										<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
											<div>
												<label className="block text-gray-700 text-sm font-semibold mb-2">
													{t("form.advanced.screen")}
												</label>
												<select
													value={log.screen_id}
													onChange={(e) =>
														handleLogChange(index, "screen_id", e.target.value)
													}
													disabled={!screensReady}
													className="appearance-none border rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md border-gray-300"
												>
													{screenOptions.map((screen) => (
														<option
															key={screen.Monitor_ID}
															value={screen.Monitor_ID}
														>
															{screen.Monitor_Name}
														</option>
													))}
												</select>
											</div>

											<Input
												label={t("form.advanced.hoursLogged")}
												type="number"
												min="0"
												step="0.1"
												value={log.hours_logged}
												onChange={(e) =>
													handleLogChange(
														index,
														"hours_logged",
														parseFloat(e.target.value) || 0,
													)
												}
											/>

											<Input
												label={t("form.advanced.adPlays")}
												type="number"
												min="0"
												value={log.ad_plays}
												onChange={(e) =>
													handleLogChange(
														index,
														"ad_plays",
														parseInt(e.target.value) || 0,
													)
												}
											/>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default AdvancedFilterSection;
